const DATE = new Intl.DateTimeFormat("en-GB", {
  day: "2-digit",
  month: "short",
  year: "numeric",
});

const DATETIME = new Intl.DateTimeFormat("en-GB", {
  day: "2-digit",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
});

const TIME = new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit" });

export function fmtDate(v: string | Date | null | undefined): string {
  if (!v) return "—";
  const d = typeof v === "string" ? new Date(v.length === 10 ? v + "T00:00:00" : v) : v;
  return Number.isNaN(d.getTime()) ? "—" : DATE.format(d);
}

const DATE_SHORT = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short" });

/** No year — for compact list rows where the year rarely adds anything. */
export function fmtDateShort(v: string | Date | null | undefined): string {
  if (!v) return "—";
  const d = typeof v === "string" ? new Date(v.length === 10 ? v + "T00:00:00" : v) : v;
  return Number.isNaN(d.getTime()) ? "—" : DATE_SHORT.format(d);
}

export function fmtDateTime(v: string | Date | null | undefined): string {
  if (!v) return "—";
  const d = typeof v === "string" ? new Date(v) : v;
  return Number.isNaN(d.getTime()) ? "—" : DATETIME.format(d);
}

export function fmtTime(v: string | Date | null | undefined): string {
  if (!v) return "—";
  const d = typeof v === "string" ? new Date(v) : v;
  return Number.isNaN(d.getTime()) ? "—" : TIME.format(d);
}

export function fmtMiles(v: number | null | undefined): string {
  return v == null ? "—" : `${new Intl.NumberFormat("en-GB").format(v)} mi`;
}

export function fmtMoney(v: number | string | null | undefined): string {
  if (v == null) return "—";
  const n = typeof v === "string" ? Number(v) : v;
  if (Number.isNaN(n)) return "—";
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    maximumFractionDigits: 0,
  }).format(n);
}

/** "3 days ago", "in 2 weeks" — for last-seen and due dates. */
export function fmtRelative(v: string | Date | null | undefined): string {
  if (!v) return "—";
  const d = typeof v === "string" ? new Date(v) : v;
  if (Number.isNaN(d.getTime())) return "—";
  const rtf = new Intl.RelativeTimeFormat("en-GB", { numeric: "auto" });
  const diffMs = d.getTime() - Date.now();
  const mins = Math.round(diffMs / 60000);
  if (Math.abs(mins) < 60) return rtf.format(mins, "minute");
  const hours = Math.round(mins / 60);
  if (Math.abs(hours) < 24) return rtf.format(hours, "hour");
  const days = Math.round(hours / 24);
  if (Math.abs(days) < 30) return rtf.format(days, "day");
  return fmtDate(d);
}

/** Registration plates read better spaced, but never mangle a short one. */
export function fmtPlate(reg: string | null | undefined): string {
  if (!reg) return "—";
  const clean = reg.replace(/\s+/g, "").toUpperCase();
  return clean.length === 7 ? `${clean.slice(0, 4)} ${clean.slice(4)}` : clean;
}

/**
 * Manufacturer names arrive from DVLA in full caps ("MERCEDES-BENZ",
 * "BMW"). Shown verbatim they shout next to the status badges, but a
 * naive title-case turns BMW into "Bmw".
 *
 * A word with no vowel is an acronym, so it keeps its capitals; anything
 * else is title-cased. That covers the marques actually on the fleet
 * (BMW and MG stay upper; Mercedes-Benz, Citroen, Volkswagen, Renault,
 * Fiat, Polestar all title-case), and degrades to merely-unstyled rather
 * than wrong on anything it has not met. Hyphens are word boundaries, so
 * "MERCEDES-BENZ" becomes "Mercedes-Benz" and not "Mercedes-benz".
 */
export function fmtManufacturer(make: string | null | undefined): string {
  if (!make) return "";
  return make
    .toLowerCase()
    .split(/([\s-]+)/)
    .map((part) => {
      if (/^[\s-]+$/.test(part) || part === "") return part;
      if (!/[aeiou]/.test(part)) return part.toUpperCase();
      return part[0]!.toUpperCase() + part.slice(1);
    })
    .join("");
}

export function initials(name: string | null | undefined): string {
  if (!name) return "?";
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join("");
}

/** Local datetime string an <input type="datetime-local"> understands. */
export function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function toISODate(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function startOfWeek(d: Date): Date {
  const out = new Date(d);
  const day = (out.getDay() + 6) % 7; // Monday = 0
  out.setDate(out.getDate() - day);
  out.setHours(0, 0, 0, 0);
  return out;
}

export function addDays(d: Date, n: number): Date {
  const out = new Date(d);
  out.setDate(out.getDate() + n);
  return out;
}

export function startOfDay(d: Date): Date {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  return out;
}

// Add one calendar year (keeps the same month/day), not 365 days — so a term
// that spans a leap year still lands on the same date next year.
export function addOneYear(d: Date): Date {
  return new Date(d.getFullYear() + 1, d.getMonth(), d.getDate());
}
